import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
const EASE = [0.23, 1, 0.32, 1] as const;

/** Static market-map layout. Coordinates are in a 400x460 viewBox. */
const NODES = [{
  x: 62,
  y: 74,
  r: 4,
  kind: 'company'
}, {
  x: 148,
  y: 44,
  r: 3,
  kind: 'profile'
}, {
  x: 236,
  y: 88,
  r: 4,
  kind: 'company'
}, {
  x: 330,
  y: 58,
  r: 3,
  kind: 'profile'
}, {
  x: 44,
  y: 172,
  r: 3,
  kind: 'profile'
}, {
  x: 132,
  y: 148,
  r: 6,
  kind: 'match'
}, {
  x: 214,
  y: 196,
  r: 3,
  kind: 'profile'
}, {
  x: 316,
  y: 158,
  r: 4,
  kind: 'company'
}, {
  x: 88,
  y: 258,
  r: 3,
  kind: 'profile'
}, {
  x: 190,
  y: 286,
  r: 6,
  kind: 'match'
}, {
  x: 288,
  y: 250,
  r: 3,
  kind: 'profile'
}, {
  x: 356,
  y: 300,
  r: 3,
  kind: 'profile'
}, {
  x: 58,
  y: 356,
  r: 4,
  kind: 'company'
}, {
  x: 152,
  y: 392,
  r: 3,
  kind: 'profile'
}, {
  x: 252,
  y: 358,
  r: 6,
  kind: 'match'
}, {
  x: 340,
  y: 404,
  r: 3,
  kind: 'profile'
}] as const;
const EDGES = [[0, 1], [1, 2], [2, 3], [0, 4], [1, 5], [5, 2], [2, 7], [5, 6], [4, 8], [5, 9], [6, 9], [7, 10], [10, 11], [8, 12], [9, 13], [9, 14], [10, 14], [14, 15], [12, 13]] as const;
const COLOR = {
  company: '#8B948F',
  profile: '#4A5654',
  match: '#FF6846'
} as const;
export function SearchIntelligenceVisual({
  caption
}: {
  caption: string;
}) {
  const reduce = useReducedMotion();
  return <div className="relative w-full" data-id="element-484" data-mp="3012j1gk4wbnz">
      <div className="relative border border-line-dark bg-ink-light/40 overflow-hidden" data-id="element-485" data-mp="2397ba8s20d58">
        {/* Interface chrome */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-line-dark" data-id="element-486" data-mp="27bc2l0v14xop">
          <span className="esca-eyebrow text-muted-light" data-id="element-487" data-mp="2c8c89anyvyfa">Market Map</span>
          <div className="flex items-center gap-4 text-[0.6875rem] tracking-wide text-muted" data-id="element-488" data-mp="2h8zyvv8aad8z">
            <span className="flex items-center gap-1.5" data-id="element-489" data-mp="3nosrlf2n6z7f">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              background: COLOR.company
            }} data-id="element-490" data-mp="3noapw2x7nss4" />
              companies
            </span>
            <span className="flex items-center gap-1.5" data-id="element-491" data-mp="32xwzjroc1keu">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              background: COLOR.match
            }} data-id="element-492" data-mp="2y0wthgrhghnq" />
              matches
            </span>
          </div>
        </div>

        <svg viewBox="0 0 400 460" className="w-full h-auto block" role="img" aria-label={caption} data-id="element-493" data-mp="37yb9jf9hv4fi">
          <defs data-id="element-494" data-mp="2k6w43dmf8701">
            <pattern id="esca-grid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-495" data-mp="2f9xb4yehu63f">
              <path d="M40 0H0V40" fill="none" stroke="#1E2A28" strokeWidth="1" data-id="element-496" data-mp="2u0cxwhmk5m3p" />
            </pattern>
          </defs>
          <rect width="400" height="460" fill="url(#esca-grid)" data-id="element-497" data-mp="21gxweymjcfwx" />

          {EDGES.map(([a, b], i) => {
          const from = NODES[a];
          const to = NODES[b];
          const highlighted = from.kind === 'match' || to.kind === 'match';
          return <motion.line key={`e-${i}`} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={highlighted ? '#FF6846' : '#2A3634'} strokeWidth={highlighted ? 1 : 1} strokeOpacity={highlighted ? 0.45 : 1} initial={reduce ? false : {
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.3 + i * 0.035,
            ease: EASE
          }} data-id="element-498" data-mp="gt55tciqrblx" data-mpi={i} />;
        })}

          {NODES.map((n, i) => <g key={`n-${i}`} data-id="element-499" data-mp="36gclstr808c2" data-mpi={i}>
              {n.kind === 'match' && <motion.circle cx={n.x} cy={n.y} r={n.r + 6} fill="none" stroke="#FF6846" strokeWidth="1" strokeOpacity="0.35" initial={reduce ? false : {
            scale: 0.7,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} style={{
            transformOrigin: `${n.x}px ${n.y}px`
          }} transition={{
            duration: 0.4,
            delay: 1.05 + i * 0.02,
            ease: EASE
          }} data-id="element-500" data-mp="3air67iajfg5g" />}
              <motion.circle cx={n.x} cy={n.y} r={n.r} fill={COLOR[n.kind]} initial={reduce ? false : {
            scale: 0.6,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} style={{
            transformOrigin: `${n.x}px ${n.y}px`
          }} transition={{
            duration: 0.35,
            delay: 0.15 + i * 0.045,
            ease: EASE
          }} data-id="element-501" data-mp="8lu0zoyz4372" />
            </g>)}
        </svg>

        <div className="flex items-center justify-between px-4 py-3 border-t border-line-dark" data-id="element-502" data-mp="zq7rcax54bvg">
          <span className="text-[0.6875rem] text-muted" data-id="element-503" data-mp="13r9rctyvsryh">{caption}</span>
          <span className="text-[0.6875rem] tabular-nums text-muted-light" data-id="element-504" data-mp="18r5u6pvuedcg">
            16 / 156
          </span>
        </div>
      </div>
    </div>;
}